/**
 * Locked node renderer module for styling locked nodes across all browsers
 */

/**
 * Styles locked nodes with appropriate visual elements:
 * - Adds blue backing rectangle 
 * - Adds lock icon
 * - Adds placeholder lines
 * @returns {void}
 */
function styleLockedNodes() {
  console.log('[DEBUG] Starting styleLockedNodes function');
  
  // Find all locked nodes in the diagram
  const lockedNodes = document.querySelectorAll('.mermaid .node.locked');
  console.log('[DEBUG] Found locked nodes:', lockedNodes.length);
  
  lockedNodes.forEach((node, index) => {
    console.log(`[DEBUG] Processing locked node ${index + 1}/${lockedNodes.length}`);
    
    // Find the rect element (this is our grey rect)
    const rectElem = node.querySelector('rect.basic');
    if (!rectElem) {
      console.log(`[DEBUG] Node ${index + 1} has no rect.basic element, skipping`);
      return;
    }
    
    // Get the dimensions and properties of the rectangle
    const x = parseFloat(rectElem.getAttribute('x')) || 0;
    const y = parseFloat(rectElem.getAttribute('y')) || 0;
    const width = parseFloat(rectElem.getAttribute('width')) || 0;
    const height = parseFloat(rectElem.getAttribute('height')) || 0;
    const rx = parseFloat(rectElem.getAttribute('rx')) || 4; // Default rx of 4 if not specified
    const ry = parseFloat(rectElem.getAttribute('ry')) || 4; // Default ry of 4 if not specified
    
    // For Safari, add both blue backing rectangle and lock icon
    // For Chrome/Firefox, only add the lock icon (blue backing is from CSS)
    if (!node.querySelector('.locked-node-icon')) {
      // Ensure the grey rect is fully opaque in all browsers
      rectElem.setAttribute('fill-opacity', '1');
      rectElem.style.opacity = '1';
      
      // Create the blue backing rectangle
      if (!node.querySelector('.blue-backing-rect')) {
        console.log(`[DEBUG] Adding blue backing rectangle`);
        const blueRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        blueRect.setAttribute('class', 'blue-backing-rect');
        blueRect.setAttribute('x', x - 3); // Move 3px to the left
        blueRect.setAttribute('y', y + 1); // Position 1px below top of grey box
        blueRect.setAttribute('width', width - 4); // Make width 4px less than grey box
        blueRect.setAttribute('height', height - 2); // Make height 2px less than grey box
        blueRect.setAttribute('rx', '6'); // Match diagram.css rx value of 6px
        blueRect.setAttribute('ry', '6'); // Match diagram.css ry value of 6px
        blueRect.setAttribute('fill', getComputedStyle(document.documentElement).getPropertyValue('--colors-brand-light-blue').trim()); // Light blue from brand colors
        blueRect.setAttribute('stroke', 'none'); // Remove any border
        blueRect.setAttribute('fill-opacity', '1'); // Ensure it's fully opaque
        
        // Insert the blue rect before the grey rect so it appears behind it
        node.insertBefore(blueRect, rectElem);
      }
      
      // Add a lock icon for ALL browsers
      const lockIcon = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      lockIcon.setAttribute('class', 'locked-node-icon');
      
      // Create SVG elements for the lock icon
      // First create the rect part of the lock
      const lockRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      lockRect.setAttribute('class', 'locked-node-rect');
      lockRect.setAttribute('x', '3');
      lockRect.setAttribute('y', '11');
      lockRect.setAttribute('width', '18');
      lockRect.setAttribute('height', '11');
      lockRect.setAttribute('rx', '2'); // Corner radius
      lockRect.setAttribute('ry', '2'); // Corner radius
      lockRect.setAttribute('stroke', getComputedStyle(document.documentElement).getPropertyValue('--colors-brand-gray').trim()); // Gray from brand colors
      lockRect.setAttribute('stroke-width', '2'); // Medium thickness
      lockRect.setAttribute('fill', 'none'); // No fill
      lockRect.setAttribute('stroke-linecap', 'round');
      lockRect.setAttribute('stroke-linejoin', 'round');
      
      // Then create the U shaped part at the top
      const lockShackle = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      lockShackle.setAttribute('d', 'M7 11V7a5 5 0 0 1 10 0v4');
      lockShackle.setAttribute('stroke', getComputedStyle(document.documentElement).getPropertyValue('--colors-brand-gray').trim()); // Gray from brand colors
      lockShackle.setAttribute('stroke-width', '2'); // Matching thickness with the rect
      lockShackle.setAttribute('fill', 'none'); // Use 'none' instead of 'transparent'
      lockShackle.setAttribute('stroke-linecap', 'round');
      lockShackle.setAttribute('stroke-linejoin', 'round');
      
      // Add both parts to the lock icon group
      lockIcon.appendChild(lockRect);
      lockIcon.appendChild(lockShackle);
      
      // Position the lock icon on the left side of the grey rectangle
      const lockX = x + 25; // Position 25px from the left edge of the grey box (moved right by 10px)
      const lockY = y + (height / 2); // Vertically center in the grey box
      
      // Create a small viewBox attribute to ensure proper rendering
      lockIcon.setAttribute('viewBox', '0 0 24 24');
      
      // Set the transform to position the icon - use appropriate scaling to match Firefox/Chrome
      lockIcon.setAttribute('transform', `translate(${lockX - 12}, ${lockY - 12}) scale(1)`);
      
      // Add the lock icon to the node
      node.appendChild(lockIcon);
      console.log(`[DEBUG] Lock icon added to node ${index + 1}`);
    }
    
    // For ALL browsers, create the placeholder lines
    if (!node.querySelector('.placeholder-lines-group')) {
      // Create placeholder lines below the title
      console.log(`[DEBUG] Creating placeholder lines for node ${index + 1}`);
      
      // Using title text for accurate height measurement 
      console.log(`[DEBUG] Using title element to determine placement`);
      
      // Find foreignObject elements which contain our HTML content
      const foreignObjects = node.querySelectorAll('foreignObject');
      
      if (foreignObjects.length === 0) {
        console.error(`[ERROR] No foreignObject found in node ${index + 1}, cannot create placeholder lines`);
        return;
      }
      
      // Measure title element to determine placement
      const titleResult = measureTitleElement(node, foreignObjects[0], x, y);
      
      if (titleResult) {
        console.log(`[DEBUG] Successfully measured title element using method: ${titleResult.method}`);
        
        // Create placeholder lines with the accurate title position
        createPlaceholderLinesAccurate(node, x, y, width, height, 0, titleResult.debugElements);
      } else {
        console.error(`[ERROR] Could not determine title position for node ${index + 1}, cannot create placeholder lines`);
      }
    }
  });
}

/**
 * Measures the title element to determine placement for placeholder lines
 * @param {Element} node - The SVG node element
 * @param {Element} foreignObject - The foreignObject element containing HTML
 * @param {number} x - X position of the node
 * @param {number} y - Y position of the node
 * @returns {Object|null} Object with method info and elements used for measurement
 */
function measureTitleElement(node, foreignObject, x, y) {
  console.log(`[DEBUG] Starting title positioning measurement`);
  
  try {
    // Get the foreignObject dimensions
    const objX = parseFloat(foreignObject.getAttribute('x')) || 0;
    const objY = parseFloat(foreignObject.getAttribute('y')) || 0;
    const objWidth = parseFloat(foreignObject.getAttribute('width')) || 0;
    const objHeight = parseFloat(foreignObject.getAttribute('height')) || 0;
    
    console.log(`[DEBUG] ForeignObject position: x=${objX}, y=${objY}, width=${objWidth}, height=${objHeight}`);
    
    // 1. Create a unique ID for this debug instance
    const uniqueId = 'debug-clone-' + Date.now();
    
    // 2. Clone the entire foreignObject with all content
    const clonedForeignObject = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject');
    clonedForeignObject.setAttribute('id', uniqueId);
    clonedForeignObject.setAttribute('class', 'debug-clone');
    
    // Copy attributes from the original
    clonedForeignObject.setAttribute('x', objX);
    clonedForeignObject.setAttribute('y', objY);
    clonedForeignObject.setAttribute('width', objWidth);
    clonedForeignObject.setAttribute('height', objHeight);
    
    // 3. Calculate offsets to correct position (half width/height)
    const offsetX = -objWidth / 2;
    const offsetY = -objHeight / 2;
    
    // 4. Apply transform to adjust position
    const adjustmentTransform = `translate(${offsetX}, ${offsetY})`;
    clonedForeignObject.setAttribute('transform', adjustmentTransform);
    
    console.log(`[DEBUG] Created clone with ID: ${uniqueId}, applied transform: ${adjustmentTransform}`);
    console.log(`[DEBUG] Position correction: offsetX=${offsetX}, offsetY=${offsetY}`);
    
    // Get the HTML content from the original
    const contentContainer = foreignObject.querySelector('div') || foreignObject.firstElementChild;
    if (!contentContainer) {
      console.log(`[DEBUG] No content container found in foreignObject`);
      return null;
    }
    
    // Clone the content container with all its children
    const clonedContent = contentContainer.cloneNode(true);
    
    // Find the bold element in the clone
    const boldElement = clonedContent.querySelector('b, strong');
    
    // Return early if no title found
    if (!boldElement) {
      console.log(`[ERROR] No bold title element found in node`);
      return null;
    }
      
    // 5. Create and add a stylesheet to make the cloned title invisible
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      #${uniqueId} {
        visibility: hidden !important;
        opacity: 0 !important;
      }
    `;
    document.head.appendChild(styleElement);
    
    // 6. Clean up the content - remove everything after the bold element
    // Get the parent container of the bold element
    const boldParent = boldElement.parentNode;
    
    // Remove all siblings after the bold element
    let nextSibling = boldElement.nextSibling;
    while (nextSibling) {
      const current = nextSibling;
      nextSibling = nextSibling.nextSibling;
      boldParent.removeChild(current);
    }
    
    // Now remove all nodes after the bold element's parent in the main container
    if (boldParent !== clonedContent) {
      nextSibling = boldParent.nextSibling;
      while (nextSibling) {
        const current = nextSibling;
        nextSibling = nextSibling.nextSibling;
        clonedContent.removeChild(current);
      }
    }
    
    console.log(`[DEBUG] Removed all content after bold element`);
    
    // 7. Handle pointer events to ensure we don't block interaction
    clonedContent.style.pointerEvents = 'none';
    
    // 8. Add the cloned content to our foreignObject
    clonedForeignObject.appendChild(clonedContent);
    
    // 9. Add the foreignObject to the node
    node.appendChild(clonedForeignObject);
    
    // 10. Log information about the title we found
    console.log(`[DEBUG] Found bold element in clone with text: "${boldElement.textContent.trim()}"`);
    
    // Get measurements for debugging
    const boldRect = boldElement.getBoundingClientRect();
    console.log(`[DEBUG] Bold element screen coordinates: top=${boldRect.top}, bottom=${boldRect.bottom}, height=${boldRect.height}`);
    
    // We'll let createExactDebugBox use the node's BBox and title height for positioning
    return {
      method: 'bounding-box-positioning',
      debugElements: [clonedForeignObject, styleElement]
    };
  } catch (e) {
    console.log(`[DEBUG] Error in title clone approach: ${e.message}`);
    return null;
  }
}

/**
 * Determines the scale factor between SVG coordinates and screen coordinates
 * @param {Element} node - SVG node element
 * @returns {Object|null} Object with scaleX and scaleY factors, or null if determination fails
 */
function determineSvgScale(node) {
  try {
    // Find the SVG root element
    const svgRoot = node.closest('svg');
    if (!svgRoot) {
      console.log('[ERROR] Cannot find SVG root element for scale calibration');
      return null;
    }
    
    // Create a temporary rectangle for calibration (100x100 SVG units)
    const calibrationRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    calibrationRect.setAttribute('width', '100');
    calibrationRect.setAttribute('height', '100');
    calibrationRect.setAttribute('fill', 'none');
    calibrationRect.setAttribute('stroke', 'none');
    calibrationRect.style.visibility = 'hidden';
    
    // Add it to the SVG temporarily
    svgRoot.appendChild(calibrationRect);
    
    // Measure its screen dimensions
    const rectScreenSize = calibrationRect.getBoundingClientRect();
    
    // Calculate scale factors
    const scaleX = rectScreenSize.width / 100;
    const scaleY = rectScreenSize.height / 100;
    
    console.log(`[DEBUG] SVG to screen scale factors: X=${scaleX}, Y=${scaleY}`);
    
    // Remove the calibration rectangle
    svgRoot.removeChild(calibrationRect);
    
    return { scaleX, scaleY };
  } catch (e) {
    console.log(`[ERROR] Failed to determine SVG scale factor: ${e.message}`);
    return null;
  }
}

/**
 * Creates placeholder lines using precise title positioning
 */
function createPlaceholderLinesAccurate(node, x, y, width, height, titleBottom, debugElements) {
  try {
    // Get node's bounding box for SVG coordinates
    const nodeBBox = node.getBBox();
    
    // Determine SVG to screen scale factor
    const scale = determineSvgScale(node);
    if (!scale) {
      console.log('[ERROR] Cannot create debug box without scale factor');
      return;
    }
    
    // Get the bold element from our cloned foreignObject
    const boldElement = debugElements[0].querySelector('b, strong');
    if (!boldElement) {
      console.log('[ERROR] Cannot find title element in cloned foreignObject');
      return;
    }
    
    // Title settings
    const titleTopOffset = 15; // Approximate distance from top of node to start of title
    
    // Get title height in screen coordinates
    const titleScreenHeight = boldElement.getBoundingClientRect().height;
    
    // Convert to SVG coordinates using our scale factor
    const titleSvgHeight = titleScreenHeight / scale.scaleY;
    
    console.log(`[DEBUG] Title height: ${titleScreenHeight}px in screen coordinates, ${titleSvgHeight} SVG units`);
    
    // Add a margin between title bottom and green box top (matching the top offset)
    const titleBottomMargin = 15; // Same as titleTopOffset for symmetry
    
    // Calculate the exact position for the green box to start at title bottom + margin
    const greenBoxTop = nodeBBox.y + titleTopOffset + titleSvgHeight + titleBottomMargin;
    
    // Ensure X is always positive by using 10 as minimum
    const nodeX = Math.max(10, nodeBBox.x);
    
    // Use percentage-based layout with flexible width calculation
    // Based on discovery that nodeX is the CENTER of the box
    
    // Set desired green box width as percentage of grey box
    const boxWidthPercentage = 0.75; // 75% of grey box width
    const boxWidth = Math.floor(nodeBBox.width * boxWidthPercentage);

    // Right margin fixed at 10%
    const rightMarginPercentage = 0.1; // 10% margin on right

    // Calculate required left margin percentage
    // leftMargin% + boxWidth% + rightMargin% = 100%
    const leftMarginPercentage = 1 - boxWidthPercentage - rightMarginPercentage;

    // Calculate the offset from center
    // Center is at 50%, left edge should be at leftMarginPercentage
    const horizontalOffset = Math.floor(nodeBBox.width * (leftMarginPercentage - 0.5));
    
    console.log(`[DEBUG] Layout percentages: left=${leftMarginPercentage*100}%, box=${boxWidthPercentage*100}%, right=${rightMarginPercentage*100}%`);
    
    // Bottom margin
    const boxBottomMargin = 15;
    
    // Calculate height - from bottom of title (plus margin) to near bottom of node
    const boxHeight = nodeBBox.height - titleTopOffset - titleSvgHeight - titleBottomMargin - boxBottomMargin;
    
    console.log(`[DEBUG] Exact method box dimensions: width=${boxWidth}, height=${boxHeight}, starts at y=${greenBoxTop}`);
    console.log(`[DEBUG] Title settings: topOffset=${titleTopOffset}, bottomMargin=${titleBottomMargin}, SVG height=${titleSvgHeight}`);
    
    // If height is 0 or negative, don't add the box
    if (boxHeight <= 0) {
      console.log(`[ERROR] Box height is ${boxHeight}, not enough space below title to draw debug box`);
      return;
    }
    
    // Create a group for the placeholder lines with a fixed transform
    const placeholderGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    placeholderGroup.setAttribute('class', 'placeholder-lines-group');
    
    // Create invisible container box (no border)
    const boxRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    boxRect.setAttribute('class', 'placeholder-container');
    boxRect.setAttribute('x', 0);
    boxRect.setAttribute('y', 0);
    boxRect.setAttribute('width', boxWidth);
    boxRect.setAttribute('height', boxHeight);
    boxRect.setAttribute('fill', 'none');
    boxRect.setAttribute('stroke', 'none'); // No stroke/border
    boxRect.setAttribute('stroke-width', '0');
    boxRect.setAttribute('rx', '2');
    boxRect.setAttribute('ry', '2');
    
    // Apply inline style to override any CSS rules - border hidden
    boxRect.setAttribute('style', 'fill: none !important; stroke: none !important; stroke-width: 0 !important; pointer-events: none !important;');
    
    // Add the box to the group
    placeholderGroup.appendChild(boxRect);
    
    // Create text line representations within the box
    const rectHeight = 12;    // Height of each rectangle representing a line of text
    const lineGap = 8;        // Gap between lines in SVG units
    const lineInset = 0;      // No inset from edges
    
    // Calculate number of lines that can fit in box height
    const availableHeight = boxHeight; // Use full box height, no margins
    let numLines = Math.floor(availableHeight / (lineGap + rectHeight));
    
    // Check if we can squeeze in one more line by ignoring the gap at the end
    if ((numLines * (rectHeight + lineGap) + rectHeight) <= availableHeight) {
      numLines += 1;
      console.log(`[DEBUG] Squeezing in an extra line!`);
    }
    
    console.log(`[DEBUG] Text lines: fitting ${numLines} lines in box height ${boxHeight}`);
    
    // Create gradient types for different line styles (matching Firefox/Chrome)
    
    // Create three different gradients for variety
    const createGradient = (id, stops) => {
      const gradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
      gradient.setAttribute('id', id);
      gradient.setAttribute('x1', '0%');
      gradient.setAttribute('y1', '0%');
      gradient.setAttribute('x2', '100%');
      gradient.setAttribute('y2', '0%');
      
      stops.forEach(stop => {
        const stopEl = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
        stopEl.setAttribute('offset', stop.offset);
        stopEl.setAttribute('stop-color', stop.color);
        stopEl.setAttribute('stop-opacity', stop.opacity);
        gradient.appendChild(stopEl);
      });
      
      return gradient;
    };
    
    const timestamp = Date.now();
    const gradientId1 = `line-gradient-1-${timestamp}`;
    const gradientId2 = `line-gradient-2-${timestamp}`;
    const gradientId3 = `line-gradient-3-${timestamp}`;
    
    // Get CSS variables for our gradients
    const darkGrayColor = getComputedStyle(document.documentElement).getPropertyValue('--colors-brand-dark-gray').trim();
    const grayColor = getComputedStyle(document.documentElement).getPropertyValue('--colors-brand-gray').trim();
    const lightGrayColor = getComputedStyle(document.documentElement).getPropertyValue('--colors-brand-light-gray').trim();
    const stoneColor = getComputedStyle(document.documentElement).getPropertyValue('--colors-brand-stone').trim();
    
    // Primary gradient - medium length (70%) - dark gray to gray to light gray
    const gradient1 = createGradient(gradientId1, [
      { offset: '0%', color: darkGrayColor, opacity: '1' },
      { offset: '70%', color: grayColor, opacity: '1' },
      { offset: '100%', color: lightGrayColor, opacity: '0.3' }
    ]);
    
    // Secondary gradient - shorter (50%) - gray to light gray to stone
    const gradient2 = createGradient(gradientId2, [
      { offset: '0%', color: grayColor, opacity: '1' },
      { offset: '50%', color: lightGrayColor, opacity: '1' },
      { offset: '100%', color: stoneColor, opacity: '0.2' }
    ]);
    
    // Third gradient - longer (85%) - dark gray to gray to light gray
    const gradient3 = createGradient(gradientId3, [
      { offset: '0%', color: darkGrayColor, opacity: '1' },
      { offset: '85%', color: grayColor, opacity: '1' },
      { offset: '100%', color: lightGrayColor, opacity: '0.4' }
    ]);
    
    // Add the gradient definitions to the SVG
    const svgRoot = node.closest('svg');
    const defs = svgRoot.querySelector('defs') || svgRoot.appendChild(document.createElementNS('http://www.w3.org/2000/svg', 'defs'));
    defs.appendChild(gradient1);
    defs.appendChild(gradient2);
    defs.appendChild(gradient3);
    
    // Create lines (rectangles) and add to debug group
    for (let i = 0; i < numLines; i++) {
      // Calculate Y position for this line
      const lineY = i * (rectHeight + lineGap); // No margin from top
      
      // Always use full width for lines, matching Firefox/Chrome
      const lineWidth = boxWidth;
      
      // Create rectangle element instead of line
      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('x', lineInset);
      rect.setAttribute('y', lineY);
      rect.setAttribute('width', lineWidth);
      rect.setAttribute('height', rectHeight); // Use consistent rectHeight
      rect.setAttribute('rx', '1'); // Rounded corners
      rect.setAttribute('ry', '1');
      rect.setAttribute('class', 'placeholder-line'); // Add a descriptive class
      
      // Set base style to prevent overrides from diagram.css
      let lineOpacity = 0.5; // Default opacity (lighter)
      let gradientToUse = gradientId1; // Default gradient
      
      // Style based on position in sequence, but use different gradients 
      // for visual variety (no height variations)
      if (i % 4 === 0) {
        // Standard line - medium length gradient
        gradientToUse = gradientId1;
        lineOpacity = 0.55; // Reduced opacity
      } else if (i % 4 === 1) {
        // Shorter gradient
        gradientToUse = gradientId2;
        lineOpacity = 0.5; // Reduced opacity
      } else if (i % 4 === 2) {
        // Longer gradient
        gradientToUse = gradientId3;
        lineOpacity = 0.45; // Reduced opacity
      } else {
        // Back to medium
        gradientToUse = gradientId1;
        lineOpacity = 0.4; // Reduced opacity
      }
      
      // Apply styles using inline style attribute with !important to prevent CSS overrides
      rect.setAttribute('style', `
        fill: url(#${gradientToUse}) !important;
        opacity: ${lineOpacity} !important;
        height: ${rectHeight}px !important;
        stroke: none !important;
        stroke-width: 0 !important;
        rx: 1px !important;
        ry: 1px !important;
        pointer-events: none !important;
      `);
      
      // Add rectangle to placeholder group
      placeholderGroup.appendChild(rect);
    }
    
    // No label needed
    
    // Position using the horizontal offset from center
    const boxGroupX = nodeX + horizontalOffset;
    const boxGroupY = greenBoxTop;
    
    console.log(`[DEBUG] Center-adjusted positioning: nodeX=${nodeX}, horizontalOffset=${horizontalOffset}, width=${boxWidth}`);
    placeholderGroup.setAttribute('transform', `translate(${boxGroupX}, ${boxGroupY})`);
    
    // Add the placeholder group to the node
    node.appendChild(placeholderGroup);
    
    console.log(`[DEBUG] Placeholder lines positioned with transform: translate(${boxGroupX}, ${boxGroupY})`);
  } catch (e) {
    console.error(`[ERROR] Failed to create placeholder lines: ${e.message}`);
    // No fallback - if we encounter any errors, we simply don't draw the placeholder lines
  }
}


function setupNodeStyles() {
  console.log('Setting up unified locked node styles');
  
  // Add universal styles for locked nodes
  const styleElement = document.createElement('style');
  styleElement.textContent = `
    /* Common styles for locked nodes */
    .mermaid .node.locked rect.basic {
      fill: var(--colors-brand-stone) !important;
      fill-opacity: 1 !important; /* Ensure it's fully opaque */
      opacity: 1 !important; /* Ensure it's fully opaque */
      stroke: none !important; /* No stroke/border */
    }
    
    /* Disable pseudo-elements that might interfere */
    .mermaid .node.locked *::before,
    .mermaid .node.locked *::after {
      display: none !important;
      content: none !important;
    }
  `;
  
  document.head.appendChild(styleElement);
}

// Run immediately as soon as the script loads, don't wait for DOMContentLoaded
function init() {
  // In case DOMContentLoaded already fired
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupNodeStyles);
  } else {
    // Run immediately if already loaded
    setupNodeStyles();
  }
  
  // Add early styling to ensure consistent rendering
  const earlyStyle = document.createElement('style');
  earlyStyle.textContent = `
    /* Consistent styling for locked nodes */
    .mermaid .node.locked rect.basic {
      fill: var(--colors-brand-stone) !important;
      fill-opacity: 1 !important;
      opacity: 1 !important;
      stroke: none !important;
    }
    
    /* Disable pseudo-elements */
    .mermaid .node.locked *::before,
    .mermaid .node.locked *::after {
      display: none !important;
      content: none !important;
    }
    
    /* Title text styling */
    .mermaid .node.locked .nodeLabel b,
    .mermaid .node.locked .nodeLabel strong,
    .mermaid .node.locked text tspan:first-child {
      fill: var(--colors-brand-dark-gray) !important;
      color: var(--colors-brand-dark-gray) !important;
    }
    
    /* Make all text except title transparent */
    .mermaid .node.locked .nodeLabel {
      color: transparent !important;
      fill: transparent !important;
    }
  `;
  document.head.appendChild(earlyStyle);
}

// Run immediately
init();

// Export the public API
export default {
  setupNodeStyles,
  styleLockedNodes
};